import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { PageLogMes } from '@home/utils/buriedPointCfg';
import { buriedPointGame } from '@home/utils/buriedPoint';
import { StudioContext } from '@/pages/home/pages/studio/context';
import StudioState from '@/pages/home/pages/studio/components/StudioState';
import InGameNoPermissionState from '@/pages/home/pages/studio/inGameAnalytics/components/InGameNoPermissionState';
import { TAccessList } from '@/pages/home/pages/studio/type';
import Loading from '@/pages/dashboard/components/Loadings';
import { getGameLastDate, getSelectorList } from '@home/services/studio';
import SingleSelect from '@home/components/SingleSelect';
import { getName } from '@home/utils/common';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';

const InGameAnalyticsFilter = (props: any) => {
  const {
    children,
    exist,
    daySub = 1,
    className,
    period = 'daily',
    metrics = ['units_number'],
    lastDateKey = 'units_number',
    filters,
    style,
    hasPaymentTypeFilter,
    select_key: selectKey = ['payment_type'],
    paymentTypeFilterEnName = 'Payment Type', 
    paymentTypeFilterCnName = '付费类型', 
    // test环境的值是：Payment only
    defaultVal = 'Paid only',
    paymentTypeFilterCnTooltip = 'Product Payment Type', 
    paymentTypeFilterEnTooltip = '产品付费类型',     
    paymentTypeItemTooltip = {
      'Free only': {
        en: 'Applies only to revenue/sales-related data',
        cn: '此筛选器仅复用至收入相关数据'
      },
      "Paid only": {
        en: 'Applies only to revenue/sales-related data',
        cn: '此筛选器仅复用至收入相关数据'
      },
    }, 
    paymentTypeItemTooltipPlacement= 'right',
  } = props || {};

  const { currentGame } = useContext(StudioContext);
  const { studio_code: studioCode, game_code: gameCode, pc_path_access_list: accessList } =
    currentGame || {};
  const timeZone = parseInt(currentGame?.utc_offset || '', 10) || 0;
  // 默认日期t-1
  const defaultDate = moment().subtract(daySub, 'days').utcOffset(timeZone).format('YYYY-MM-DD');
  const [filterLoading, setFilterLoading] = useState(true);
  const [lastDate, setLastDate] = useState(defaultDate);
  const [payType, setPayType] = useState<string>(defaultVal);
  const [payTypeOptions, setPayTypeOptions] = useState<{ code: string; name_en: string; name_cn: string; }[]>([]);
  const payTypeRef = useRef<string>();
  const [selectorList, setSelectorList] = useState([{}]);
  // 记录loading次数，只有首次进入时，才需要全局loading
  const loadingCount = useRef<number>(0);

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 页面打点
  useEffect(() => {
    if (!gameCode) return;
    buriedPointGame({gameName: currentGame?.game_name, ...PageLogMes.studioInGameAnalytics}, gameCode, 'game_code');
  }, [gameCode, currentGame]);

  // 是否有Sales overview或者overview daily页面
  // 是否有gameCode 判断InGameAnalyticsFilter查看权限
  // 菜单is_visit字段判断左侧菜单访问权限，pc_path_access_list对应经分权限（is_exist是否存在页面，is_visit是否有页面权限）
  const { isExist, isAccess } = useMemo(() => {
    const overviewDaily =
      accessList?.find((v: TAccessList) => v?.path === '/overview/daily') || {};
    const salesOverview =
      accessList?.find(
        (v: TAccessList) => v?.path === '/monetization/salesOverview',
      ) || {};
    const isOveviewExist = overviewDaily?.is_exist || false;
    const isSalesOverviewExist = salesOverview?.is_exist || false;
    const isSalesOverviewVisit = salesOverview?.is_visit || false;
    // 是否存在页面
    const map: any = {
      "overviewDaily": isOveviewExist,
      "salesOverview": isSalesOverviewExist,
      "overviewDailyAndsalesOverview": isOveviewExist || isSalesOverviewExist
    };
    const pageExist = map?.[exist || "overviewDailyAndsalesOverview"];
    const isExist = pageExist && gameCode;
    // 是否有salesoverview页面权限
    const isAccess = isSalesOverviewVisit;
    return { isExist, isAccess };
  }, [accessList, exist, gameCode]);

  // 接口获取的lastDate最新有数据的时间点
  const getLastDate = async (payTypeValue?: string) => {
    setFilterLoading(true);
    try {
      const lastDateParams: any = {
        period,
        metrics,
        studio_code: studioCode,
        game_code: gameCode,
        start_time: defaultDate,
        end_time: defaultDate,
        is_confirm: false,
        filters: filters || {
          "zone_list": ["255"],
          "market_list": ["255"],
          "channel_list": ["255"],
          "os_list": ["255"],
          "lang_list": ["255"],
        },
        system: "studio",
      };

      // 依赖筛选器接口
      if (payTypeValue) {
        lastDateParams.logical_flag = "platform_revenue_v2";
        lastDateParams.filters.custom_filters = [
          {
            "key": "payment_type",
            "filter_list": [payTypeValue || 'All']
          }
        ];
        payTypeRef.current = payTypeValue;
      }

      const res = await getGameLastDate(lastDateParams);
      const { latest_date: dateMap } = res;
      const lastDate = dateMap?.[lastDateKey];
      setLastDate(lastDate);
    } catch (error) {
      console.log("🚀 ~ getLastDate ~ error:", error);
    }
    loadingCount.current += 1;
    setFilterLoading(false);
  };

  // 获取页面所需前置数据
  const getPreData = async () => {
    setFilterLoading(true);
    try {
      let payTypeOpts;
      // 获取付费筛选器选项
      if (hasPaymentTypeFilter) {
        const params = {
          studio_code: studioCode,
          game_codes: [gameCode],
          select_key: selectKey
        };
        const res = await getSelectorList(params);
        // 把所有游戏的筛选器合并
        const mergedSelectList = (() => {
          const selectMap = new Map();
                
          for (const game of res) {
            for (const select of game?.select_list || []) {
              const groupKey = String(select?.select_key).toLowerCase();
              const codeMap = selectMap?.get(groupKey) || new Map();
                
              for (const item of select?.select_list) {
                const itemKey = item?.code?.toString();
                if (itemKey && !codeMap?.has(itemKey)) {
                  codeMap?.set(itemKey, { ...item });
                }
              }
                
              selectMap?.set(groupKey, codeMap);
            }
          }
                
          return Array.from(selectMap, ([select_key, codeMap]) => ({
            select_key,
            select_list: Array.from(codeMap.values())
          }));
        })();
        setSelectorList(mergedSelectList);
        payTypeOpts = mergedSelectList?.find((item: any) => item.select_key === 'payment_type');
        const selectList = payTypeOpts?.select_list || [];
        // ALL排序在第一个
        let allOpt;
        const otherOpts: any[] = [];
        selectList?.forEach((item: any) => {
          if (item.code === 'All') {
            allOpt = item;
          } else {
            otherOpts.push(item);
          }
        });
        if (allOpt) otherOpts.unshift(allOpt);
        setPayTypeOptions(otherOpts);
        setPayType(defaultVal || otherOpts[0]?.code || 'All');
        payTypeOpts = otherOpts;
      }
      getLastDate(defaultVal || payTypeOpts?.[0]?.code);
    } catch (error) {
      console.log("🚀 ~ getPreData ~ error:", error);
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    if (!gameCode || !(isExist && isAccess)) return;
    getPreData();
  }, [gameCode, studioCode,defaultVal, isExist, isAccess]);

  // 切换选项时，需重新查询指标最新日期
  useEffect(() => {
    if (filterLoading) return;
    getLastDate(payType);
  }, [payType]);

  const newPayTypeOptions = useMemo(() => payTypeOptions?.map(item => {
    const { code, name_en, name_cn } = item;
    // 特殊处理all需要展示成全部
    const name = getName(isEn, name_en, name_cn === 'All' ? '全部' : name_cn);
    const tooltipText = paymentTypeItemTooltip?.[name_en]?.[isEn ? 'en' : 'cn'] || '';
    return {
      value: code,
      text: name,
      tooltip: tooltipText,
      tooltipPlacement: paymentTypeItemTooltipPlacement
    };
  }), [isEn, payTypeOptions, paymentTypeItemTooltip, paymentTypeItemTooltipPlacement]);

  return <>
    {isExist && isAccess ?
      (<div className={classnames(className, 'dbh-ingame-anlytics-container')} style={style}>
        {
          hasPaymentTypeFilter && <div className="dbo-ingame-analytics">
            <div className="dbh-studio-operate dbh-studio-home-filter">
              <SingleSelect
                className='dbh-studio-home-paymemt-type-select'
                matchButtonWidth
                options={newPayTypeOptions}
                value={payType}
                searchable={false}
                locale={locale}
                onChange={setPayType}
                name={getName(isEn, paymentTypeFilterEnName, paymentTypeFilterCnName)}
                tooltip={getName(isEn, paymentTypeFilterCnTooltip, paymentTypeFilterEnTooltip)}
              />
            </div>
          </div>
        }
        {filterLoading && loadingCount.current < 1 ?
          <Loading /> : children &&
          React.Children.map(children, (child) => React.cloneElement(child, { currentGame, lastDate, payType: payTypeRef.current, selectorList }))}
      </div>
      ) : !isExist ? (
        <StudioState />
      ) : (
        <InGameNoPermissionState />
      )}
  </>;
};

export default InGameAnalyticsFilter;
